import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Save,
  RotateCcw,
  Settings2,
  CalendarDays,
  ListOrdered,
  Database,
  CheckCircle2
} from 'lucide-react';

export const PaymentConfig = () => {
  const navigate = useNavigate();
  const { user } = getAuth();
  
  const [currentAccount, setCurrentAccount] = useState(() => {
    const str = localStorage.getItem('current_account');
    return str ? JSON.parse(str) : null;
  });
  
  useEffect(() => {
    const checkAccount = () => {
      const str = localStorage.getItem('current_account');
      const acc = str ? JSON.parse(str) : null;
      if (acc?.id && acc.id !== currentAccount?.id) {
        setCurrentAccount(acc);
      }
    };
    checkAccount();
    const interval = setInterval(checkAccount, 500);
    const timeout = setTimeout(() => clearInterval(interval), 10000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [currentAccount?.id]);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    bbf_rule_enabled: true,
    bbf_gap_days: 25,
    billing_sequence_enabled: true,
    billing_sequence_method: 'bill_count',
    gl_validation_enabled: false,
  });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch configuration
  const fetchConfig = useCallback(async () => {
    if (!currentAccount?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/payment-verification/v2/config?account_id=${currentAccount.id}`);
      const configData = response.data.config;
      setConfig(configData);
      setOriginalConfig(configData);
      setHasChanges(false);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  }, [currentAccount?.id]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Track changes
  useEffect(() => {
    if (originalConfig) {
      const changed = JSON.stringify(config) !== JSON.stringify(originalConfig);
      setHasChanges(changed);
    }
  }, [config, originalConfig]);

  // Handle save
  const handleSave = async () => {
    if (!currentAccount?.id || !user?.id) return;
    
    setSaving(true);
    try {
      const response = await api.put(
        `/payment-verification/v2/config?account_id=${currentAccount.id}&user_id=${user.id}`,
        {
          bbf_rule_enabled: config.bbf_rule_enabled,
          bbf_gap_days: config.bbf_gap_days,
          billing_sequence_enabled: config.billing_sequence_enabled,
          billing_sequence_method: config.billing_sequence_method,
          gl_validation_enabled: config.gl_validation_enabled,
        }
      );
      
      const updatedConfig = response.data.config;
      setConfig(updatedConfig);
      setOriginalConfig(updatedConfig);
      setHasChanges(false);
      toast.success('Configuration saved successfully');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Handle reset
  const handleReset = async () => {
    if (!currentAccount?.id || !user?.id) return;
    if (!window.confirm('Are you sure you want to reset to default settings?')) return;
    
    setSaving(true);
    try {
      const response = await api.post(
        `/payment-verification/v2/config/reset?account_id=${currentAccount.id}&user_id=${user.id}`
      );
      
      const resetConfig = response.data.config;
      setConfig(resetConfig);
      setOriginalConfig(resetConfig);
      setHasChanges(false);
      toast.success('Configuration reset to defaults');
    } catch (error) {
      console.error('Error resetting config:', error);
      toast.error('Failed to reset configuration');
    } finally {
      setSaving(false);
    }
  };

  // Update config field
  const updateConfig = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-6 md:px-12 py-8" data-testid="payment-config-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/payment-verification')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Payment Configuration</h1>
              <p className="text-muted-foreground">
                Configure validation rules for payment file processing
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 max-w-3xl">
          {/* BBF Rule Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5" />
                BBF (Balance Brought Forward) Rule
              </CardTitle>
              <CardDescription>
                Configure when the previous balance should be included in the calculated amount.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable BBF Rule</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, BBF is only included if specific conditions are met.
                  </p>
                </div>
                <Switch
                  checked={config.bbf_rule_enabled}
                  onCheckedChange={(checked) => updateConfig('bbf_rule_enabled', checked)}
                  data-testid="bbf-rule-toggle"
                />
              </div>
              
              {config.bbf_rule_enabled && (
                <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                  <Label htmlFor="bbf_gap_days">Gap Days Threshold</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    BBF is included only if days between bills exceeds this threshold AND the due date has passed.
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      id="bbf_gap_days"
                      type="number"
                      min="1"
                      max="90"
                      value={config.bbf_gap_days}
                      onChange={(e) => updateConfig('bbf_gap_days', parseInt(e.target.value) || 25)}
                      className="w-24"
                      data-testid="bbf-gap-days-input"
                    />
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Example: With a 25-day threshold, if Bill A is issued on Jan 1 and Bill B on Jan 30 (29 days gap), 
                    BBF will be included in Bill B's calculation only if Jan 30 is past Bill A's due date.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Billing Sequence Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ListOrdered className="h-5 w-5" />
                Billing Sequence Detection
              </CardTitle>
              <CardDescription>
                Configure how the system determines if a bill is the 1st, 2nd, or 3rd+ for an account.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Billing Sequence</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, different calculation rules apply based on bill sequence.
                  </p>
                </div>
                <Switch
                  checked={config.billing_sequence_enabled}
                  onCheckedChange={(checked) => updateConfig('billing_sequence_enabled', checked)}
                  data-testid="billing-sequence-toggle"
                />
              </div>
              
              {config.billing_sequence_enabled && (
                <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
                  <Label htmlFor="billing_sequence_method">Detection Method</Label>
                  <Select
                    value={config.billing_sequence_method}
                    onValueChange={(value) => updateConfig('billing_sequence_method', value)}
                  >
                    <SelectTrigger id="billing_sequence_method" className="w-full" data-testid="billing-sequence-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bill_count">
                        <div className="flex flex-col">
                          <span className="font-medium">Bill Count (Recommended)</span>
                          <span className="text-xs text-muted-foreground">Count previous bills in external database</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="account_start">
                        <div className="flex flex-col">
                          <span className="font-medium">Account Start Date</span>
                          <span className="text-xs text-muted-foreground">Compare issue date to account start date</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="both">
                        <div className="flex flex-col">
                          <span className="font-medium">Combined</span>
                          <span className="text-xs text-muted-foreground">Account start primary, bill count fallback</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <div className="mt-4 space-y-2 text-sm">
                    <p className="font-medium">Sequence Rules:</p>
                    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                      <li><span className="font-medium text-foreground">FIRST_BILL:</span> Base charges only (no BBF)</li>
                      <li><span className="font-medium text-foreground">SECOND_BILL:</span> Apply BBF rule based on gap days</li>
                      <li><span className="font-medium text-foreground">THIRD_PLUS:</span> Apply BBF rule based on gap days</li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* GL Validation Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                GL Code Validation
              </CardTitle>
              <CardDescription>
                Validate GL codes against a pre-configured list.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable GL Validation</Label>
                  <p className="text-sm text-muted-foreground">
                    When enabled, GL codes in the payment file will be validated against the allowed list.
                  </p>
                </div>
                <Switch
                  checked={config.gl_validation_enabled}
                  onCheckedChange={(checked) => updateConfig('gl_validation_enabled', checked)}
                  data-testid="gl-validation-toggle"
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
              data-testid="reset-config-button"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-sm text-muted-foreground">Unsaved changes</span>
              )}
              <Button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                data-testid="save-config-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save Configuration
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default PaymentConfig;
